import { loc } from '../../data/cards'
import { birthIsoRange } from '../../../../shared/birth.ts'
import type { Lang } from '../../data/cards'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMainButton } from '../../components/MainButton'
import { natalChart, planetName, PLANETS, type NatalChart } from '../../../../shared/astro.ts'
import type { Place } from '../../../../shared/places.ts'
import { SIGNS } from '../../data/signs'
import { api, ApiError } from '../../lib/api'
import { haptic } from '../../lib/telegram'
import { useSirr } from '../../store'
import { InterpretationBlock } from './Match'
import { PlacePicker } from './PlacePicker'

/** Границы поля даты. Считаются один раз: за сеанс год не меняется. */
export const BIRTH_RANGE = birthIsoRange()

type Text =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; text: string }
  | { status: 'failed' }
  /* Дневной лимит исчерпан. Отдельно от 'failed' намеренно: предлагать
     «Повторить» бессмысленно, запрос будет отклоняться до завтра. */
  | { status: 'limit' }

const WHEEL = 268
const CENTER = WHEEL / 2

/** Экранный угол: Асцендент слева, дальше знаки против часовой стрелки. */
function pointAt(longitude: number, rotation: number, radius: number) {
  const angle = ((180 + (longitude - rotation)) * Math.PI) / 180
  return { x: CENTER + radius * Math.cos(angle), y: CENTER - radius * Math.sin(angle) }
}

export function Natal() {
  const { profile, lang, t, update } = useSirr()

  const [date, setDate] = useState(() =>
    profile.birth
      ? `${profile.birth.year}-${String(profile.birth.month).padStart(2, '0')}-${String(profile.birth.day).padStart(2, '0')}`
      : '',
  )
  const [time, setTime] = useState(() =>
    profile.birth?.hour != null
      ? `${String(profile.birth.hour).padStart(2, '0')}:${String(profile.birth.minute ?? 0).padStart(2, '0')}`
      : '',
  )
  const [noTime, setNoTime] = useState(() => profile.birth?.timeUnknown ?? false)
  const [place, setPlace] = useState<Place | null>(() => profile.birth?.place ?? null)
  const [shown, setShown] = useState(false)
  const [text, setText] = useState<Text>({ status: 'idle' })

  const parsed = useMemo(() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
    if (!m) return null
    const year = Number(m[1])
    if (year < 1900 || year > 2100) return null
    const tm = /^(\d{2}):(\d{2})$/.exec(time)
    return {
      year,
      month: Number(m[2]),
      day: Number(m[3]),
      hour: noTime || !tm ? null : Number(tm[1]),
      minute: noTime || !tm ? null : Number(tm[2]),
      place,
      timeUnknown: noTime,
    }
  }, [date, time, noTime, place])

  const ready = parsed !== null && place !== null && (noTime || /^\d{2}:\d{2}$/.test(time))

  const chart: NatalChart | null = useMemo(() => {
    if (!parsed || !parsed.place) return null
    const place = parsed.place
    return natalChart({
      year: parsed.year,
      month: parsed.month,
      day: parsed.day,
      hour: parsed.hour,
      minute: parsed.minute,
      latitude: place.latitude,
      longitude: place.longitude,
      timeZone: place.timeZone,
    })
  }, [parsed])

  useEffect(() => {
    setShown(false)
    setText({ status: 'idle' })
  }, [date, time, noTime, place])

  const requestText = useCallback(async () => {
    if (!parsed) return
    setText({ status: 'loading' })
    try {
      const res = await api.astro({ kind: 'natal', lang, a: parsed, goals: profile.goals })
      // Счётчик раскладов Таро здесь не трогаем: у астрологии он свой,
      // и запись сюда стирала бы чужой остаток.
      setText({ status: 'ok', text: res.text })
      haptic.notify('success')
    } catch (err) {
      setText({ status: err instanceof ApiError && err.code === 'limit_reached' ? 'limit' : 'failed' })
    }
  }, [parsed, lang, profile.goals])

  const build = () => {
    if (!ready || !parsed) return
    haptic.impact('medium')
    setShown(true)
    update({ birth: parsed })
    void requestText()
  }

  useMainButton({
    label: shown ? t('rebuildChart') : t('buildChart'),
    disabled: !ready,
    loading: text.status === 'loading',
    onClick: build,
  })

  const rotation = chart?.angles?.asc ?? 0

  // Круг нарисован в пикселях, поэтому на узком экране ужимаем его целиком.
  const [scale, setScale] = useState(1)
  const boxRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = boxRef.current
    if (!el) return
    const fit = () => setScale(Math.min(1, el.clientWidth / WHEEL))
    fit()
    const observer = new ResizeObserver(fit)
    observer.observe(el)
    return () => observer.disconnect()
  }, [shown])

  // Планеты рядом друг с другом сдвигаем внутрь, иначе глифы слипаются.
  const dots = useMemo(() => {
    if (!chart) return []
    const sorted = [...chart.placements].sort((a, b) => a.longitude - b.longitude)
    let level = 0
    let prev = -999
    return sorted.map((p) => {
      const gap = p.longitude - prev
      level = gap < 9 ? Math.min(level + 1, 2) : 0
      prev = p.longitude
      return { ...p, radius: 96 - level * 20 }
    })
  }, [chart])

  return (
    <>
      <div className="stack" style={{ marginBottom: 18 }}>
        <Row label={t('birthDate')}>
          <input
            type="date"
            value={date}
            // Границы общие с нативом и сервером: до этого веб разрешал дату
            // рождения в будущем, вплоть до 2100 года.
            min={BIRTH_RANGE.min}
            max={BIRTH_RANGE.max}
            onChange={(e) => setDate(e.target.value)}
            style={inputStyle}
          />
        </Row>

        <Row label={t('birthTime')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              type="time"
              value={time}
              disabled={noTime}
              onChange={(e) => setTime(e.target.value)}
              style={{ ...inputStyle, flex: 1, opacity: noTime ? 0.4 : 1 }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-muted)' }}>
              <input
                type="checkbox"
                checked={noTime}
                onChange={(e) => {
                  haptic.select()
                  setNoTime(e.target.checked)
                }}
                style={{ accentColor: 'var(--gold)' }}
              />
              {t('timeUnknown')}
            </label>
          </div>
        </Row>

        <PlacePicker value={place} onChange={setPlace} />
      </div>

      {!ready && (
        <p style={{ textAlign: 'center', color: 'var(--ink-faint)', fontSize: 13 }}>{t('fillBirth')}</p>
      )}

      {shown && chart && (
        <div style={{ animation: 'fu .6s both' }}>
          {/* ── Круг карты ─────────────────────────────────────── */}
          <div ref={boxRef} style={{ width: '100%', height: WHEEL * scale, marginBottom: 18 }}>
          <div
            style={{
              width: WHEEL,
              height: WHEEL,
              position: 'relative',
              margin: scale < 1 ? 0 : '0 auto',
              transform: scale < 1 ? `scale(${scale})` : undefined,
              transformOrigin: 'top left',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: '1px solid rgba(200,162,78,.5)',
                background: 'repeating-conic-gradient(from 0deg, rgba(200,162,78,.14) 0deg 0.6deg, transparent 0.6deg 30deg)',
              }}
            />
            <div style={{ position: 'absolute', inset: 34, borderRadius: '50%', border: '1px solid rgba(200,162,78,.3)' }} />
            <div
              style={{
                position: 'absolute',
                inset: 76,
                borderRadius: '50%',
                border: '1px solid rgba(200,162,78,.2)',
                background: 'radial-gradient(circle, rgba(58,30,92,.5), rgba(18,10,31,.2))',
              }}
            />

            {/* знаки по внешнему кольцу */}
            {SIGNS.map((s, i) => {
              const pos = pointAt(i * 30 + 15, rotation, 118)
              return (
                <span
                  key={s.id}
                  style={{
                    position: 'absolute',
                    left: pos.x,
                    top: pos.y,
                    transform: 'translate(-50%, -50%)',
                    fontSize: 13,
                    color: 'var(--gold)',
                  }}
                >
                  {s.glyph}
                </span>
              )
            })}

            {/* Асцендент и MC */}
            {chart.angles && (
              <>
                <Angle label="ASC" pos={pointAt(chart.angles.asc, rotation, 60)} />
                <Angle label="MC" pos={pointAt(chart.angles.mc, rotation, 60)} />
              </>
            )}

            {/* планеты */}
            {dots.map((p) => {
              const pos = pointAt(p.longitude, rotation, p.radius)
              const glyph = PLANETS.find((x) => x.id === p.planet)!.glyph
              return (
                <span
                  key={p.planet}
                  title={planetName(p.planet, lang)}
                  style={{
                    position: 'absolute',
                    left: pos.x,
                    top: pos.y,
                    transform: 'translate(-50%, -50%)',
                    fontSize: 17,
                    color: p.retrograde ? '#e07a54' : 'var(--ink)',
                    textShadow: '0 0 10px rgba(0,0,0,.8)',
                  }}
                >
                  {glyph}
                </span>
              )
            })}
          </div>
          </div>

          {/* ── Список положений ───────────────────────────────── */}
          <div className="eyebrow" style={{ marginBottom: 10 }}>{t('placements')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {chart.placements.map((p) => (
              <div key={p.planet} style={{ display: 'flex', alignItems: 'baseline', gap: 10, fontSize: 14 }}>
                <span style={{ width: 18, color: p.retrograde ? '#e07a54' : 'var(--gold)', fontSize: 15 }}>
                  {PLANETS.find((x) => x.id === p.planet)!.glyph}
                </span>
                <span style={{ flex: 1, color: '#e2d6ef' }}>
                  {planetName(p.planet, lang)}
                  <span style={{ color: 'var(--ink-muted)' }}>
                    {' — '}
                    {loc(SIGNS[p.sign], lang)} {p.degreeInSign.toFixed(0)}°
                    {p.nearCusp && <span style={{ color: 'var(--gold)' }}>°</span>}
                  </span>
                </span>
                {p.house && (
                  <span style={{ fontSize: 11, color: 'var(--ink-faint)' }}>
                    {p.house} {t('houseShort')}
                  </span>
                )}
                {p.retrograde && <span style={{ fontSize: 11, color: '#e07a54' }}>R</span>}
              </div>
            ))}

            {chart.angles && (
              <>
                <Divider />
                <AngleRow label={t('ascendant')} sign={chart.angles.ascSign} value={chart.angles.asc} lang={lang} />
                <AngleRow label={t('midheaven')} sign={chart.angles.mcSign} value={chart.angles.mc} lang={lang} />
              </>
            )}
          </div>

          {chart.approximateTime && <Note text={t('noTimeNote')} />}
          {chart.outOfRange && <Note text={t('outOfRangeNote')} />}
          {chart.placements.some((p) => p.nearCusp) && <Note text={t('cuspNote')} />}

          <div style={{ marginTop: 14 }}>
            <InterpretationBlock text={text} onRetry={() => void requestText()} />
          </div>
        </div>
      )}
    </>
  )
}

/**
 * Общие с «Прогнозом на день»: там тоже спрашивают дату рождения, и вторая
 * копия этой рамки разъехалась бы с этой при первой же правке.
 */
export const inputStyle: React.CSSProperties = {
  background: 'transparent',
  border: 0,
  outline: 'none',
  color: 'var(--ink)',
  fontFamily: 'var(--serif)',
  fontStyle: 'italic',
  fontSize: 17,
  padding: 0,
}

export function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 14, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, color: 'var(--ink-muted)', marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  )
}

function Angle({ label, pos }: { label: string; pos: { x: number; y: number } }) {
  return (
    <span
      style={{
        position: 'absolute',
        left: pos.x,
        top: pos.y,
        transform: 'translate(-50%, -50%)',
        fontFamily: 'var(--mono)',
        fontSize: 8,
        letterSpacing: 0.5,
        color: 'rgba(214,178,94,.85)',
      }}
    >
      {label}
    </span>
  )
}

function AngleRow({ label, sign, value, lang }: { label: string; sign: number; value: number; lang: Lang }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, fontSize: 14 }}>
      <span style={{ width: 18, color: 'var(--gold)', fontSize: 15 }}>{SIGNS[sign].glyph}</span>
      <span style={{ flex: 1, color: '#e2d6ef' }}>
        {label}
        <span style={{ color: 'var(--ink-muted)' }}>
          {' — '}
          {loc(SIGNS[sign], lang)} {(value % 30).toFixed(0)}°
        </span>
      </span>
    </div>
  )
}

const Divider = () => <div style={{ height: 1, background: 'var(--line)', margin: '4px 0' }} />

function Note({ text }: { text: string }) {
  return (
    <p style={{ fontSize: 11.5, color: 'var(--ink-faint)', lineHeight: 1.45, margin: '0 0 8px' }}>{text}</p>
  )
}
